import * as React from 'react';
import NextImage from 'next/image';
import { AuthStatus } from 'constants/auth';
import { Article, Category } from 'constants/blog';
import { HOME_PAGE, SIGNUP_PAGE } from 'constants/pages';
import { useViewer } from 'hooks/useViewer';
import { ButtonLink } from 'components/Button';
import Footer from 'components/Footer';
import NewsLetterSubscriptionForm from 'components/NewsLetterSubscriptionForm';
import Post from 'components/blog/Post';
import PostsList from 'components/blog/PostsList';
import PreviewPost from 'components/blog/PreviewPost';
import TopNav from 'components/nav/TopNav/TopNav';
import Head from 'components/utilities/Head';
import PoppinsFont from 'components/utilities/PoppinsFont';

export default function BlogPage({
  categories = [],
  articles = [],
  articlesCount = 0,
}: {
  articles: Article[];
  categories: Category[];
  articlesCount: number;
}) {
  const viewer = useViewer();
  const isAnonymous = viewer?.status === AuthStatus.Anonymous;

  const [recentArticle, ...restArticles] = articles;

  return (
    <>
      <Head
        title="Blog"
        description="The best content on pickleball, tennis, and padel. Join the largest global racket sports community."
        canonicalType="currentUrlStripQuery"
      />
      <PoppinsFont />
      <TopNav
        shouldHideNavigation={false}
        shouldShowMobile
        shouldLinkToAuthPage={false}
        shouldShowAdditionalLinks={false}
        isBlur
      />

      <main className="min-h-screen overflow-x-hidden">
        <div className="md:pb-100 bg-color-bg-darkmode-primary pt-16">
          <div className="mx-auto flex max-w-[1200px] flex-col items-center px-gutter-base">
            <h1 className="mb-4 text-base font-bold text-white">The Bounce blog</h1>
            <div className="font-family-poppins font-medium text-white xs:text-[3rem] md:text-[4rem]">
              <span className="font-extrabold italic">Hit</span> different.
            </div>
            <p className="mt-2 px-12 text-center font-normal text-color-text-darkmode-secondary xs:text-[1rem] md:text-[1.5rem] lg:px-0">
              An everyday guide for amateur pickleball players.
            </p>
          </div>
          {/* Recent blog Section */}
          {recentArticle && (
            <>
              <PreviewPost
                id={recentArticle._id}
                slug={recentArticle.slug}
                tags={recentArticle.categories?.map((category) => category?.title || '')}
                title={recentArticle.title}
                createdAt={recentArticle.publishedAt}
                author={recentArticle.author}
                previewText={recentArticle.previewDescription}
                image={recentArticle?.mainImage}
              />
              <div className="w-full bg-color-bg-lightmode-primary px-6 pt-16 dark:bg-color-bg-darkmode-primary lg:px-0">
                <PostsList
                  articles={restArticles}
                  categories={categories}
                  articlesCount={articlesCount}
                />
              </div>
            </>
          )}
        </div>
        {/* Ready to Play Section */}
        <div className="w-full bg-brand-fire-500">
          <div className="relative mx-auto flex max-w-[1200px] flex-col items-center justify-between px-6 md:flex-row lg:px-0">
            <div className="flex flex-col items-center xs:mt-2 xs:pb-16 xs:pt-12 md:mt-8 md:w-1/2 md:items-start md:pb-28  md:pt-28">
              <div className="flex flex-wrap justify-center text-center font-family-poppins font-medium leading-[57.6px] text-white md:justify-start">
                <span className="xs:pr-0 xs:text-[2.5rem] md:pr-10 md:text-[3rem]">
                  JOIN <span className="font-extrabold italic">BOUNCE </span>
                </span>
                <span className="xs:pr-0 xs:text-[2.5rem] md:pr-10 md:text-[3rem]">
                  {' '}
                  TO GET THE BEST{' '}
                </span>
                <span className="font-extrabold italic xs:pr-0 xs:text-[2.5rem] md:pr-10 md:text-[3rem]">
                  {' '}
                  TOURNAMENT
                </span>
                <span className="xs:pr-0 xs:text-[2.5rem] md:pr-10 md:text-[3rem]">EXPERIENCE</span>
              </div>
              <ButtonLink
                href={isAnonymous ? SIGNUP_PAGE : HOME_PAGE}
                variant="primary-light"
                className="button-rounded-full-darkmode-only mt-10 flex h-14 items-center justify-center  text-base xs:w-[15rem] md:w-[17.5rem]"
              >
                {isAnonymous ? 'Sign up' : 'Get started'}
              </ButtonLink>
            </div>
            <div className="mt-[75px] flex w-full justify-center self-end md:mt-0 md:block md:w-[initial]">
              <img
                className="relative z-10 w-full max-w-[303px] md:max-w-[373px]"
                src="/images/tour-page/iphone-blog.png"
                alt="iphone"
              />
              <img
                className="absolute -bottom-10 right-[222px] z-0 max-w-[15.188rem] md:bottom-[-86px] md:right-[270px] md:max-w-[301px]"
                src="/images/tour-page/ball-9.png"
                alt="ball-bottom"
              />
              <img
                className="absolute right-[0px] top-[410px] z-0 w-[257px] md:right-[-150px] md:top-[-75px] md:w-[387px]"
                src="/images/tour-page/ball-8.png"
                alt="ball-top"
              />
            </div>
          </div>
        </div>
        <NewsLetterSubscriptionForm />
        <Footer />
      </main>
    </>
  );
}
